import isFunction from 'lodash/isFunction';

import getAIClient from 'Modules/ai/services/getClient';
import initApi from 'Modules/api';
import { clientApi as dataConnectClientApiConfiguration } from '@openclassrooms/data-connect-web/configuration';
import { removeCachedCurrentUser } from '@openclassrooms/data-connect-web/users';
import { getActiveLang } from '@openclassrooms/translations';
import browser from '@openclassrooms/utils/browser';
import { isActivated, getPreference } from '@openclassrooms/utils/config';
import { errorTracker } from 'Modules/event';

const debug = isActivated('debug');

if (typeof browser.window.App === 'undefined') {
  browser.window.App = {};
}

const init = {};

const App = browser.window.App;
App._launchedModules = App._launchedModules || {};

init.handleThrowingExceptionModule = function (error, moduleName) {
  browser.window.console.log(
    '%c Init error:%c ' + moduleName,
    'background: red; color: white;',
    'background: none; color: black; font-weight: bold;'
  );
  browser.window.console.log(error.stack);
  throw error;
};

init.handleUndefinedModule = function (moduleName) {
  browser.window.console.log(`Module name : ${moduleName} doesn't exist`);
};

function initModule(module, moduleIndex) {
  const { moduleToInit, initFunc, moduleName } =
    typeof module === 'string'
      ? getModuleInfoFromString(module)
      : getModuleInfoFromObject(module, moduleIndex);

  try {
    initFunc();

    if (moduleName) {
      App._launchedModules[moduleName] = moduleToInit;
    }
  } catch (e) {
    const name = moduleToInit.name || getFnName(initFunc);

    if (moduleToInit.mustBreakOnException || debug) {
      init.handleThrowingExceptionModule(e, name);
    } else {
      browser.window.console.error(`INIT '${name}' module error: ${e.message}`);
    }
  }
}

function getModuleInfoFromString(moduleName) {
  const moduleToInit = getModule(moduleName);

  if (moduleToInit) {
    return {
      moduleToInit,
      moduleName,
      initFunc: moduleToInit.init || moduleToInit.default.init,
    };
  }

  init.handleUndefinedModule(moduleName);

  return { moduleToInit, moduleName };
}

function getModuleInfoFromObject(module, moduleIndex) {
  const context = module.default || module;
  const moduleName = module.name || context.name || 'anonymous_' + moduleIndex;
  const initFunc = (isFunction(context) ? context : context.init).bind(context);

  return {
    moduleToInit: module,
    moduleName,
    initFunc,
  };
}

function getFnName(fn) {
  const f = typeof fn === 'function';
  const s = f && ((fn.name && ['', fn.name]) || fn.toString().match(/function ([^(]+)/));

  return (!f && 'not a function') || (s && s[1]) || 'anonymous';
}

export function getModule(moduleName) {
  return App._launchedModules[moduleName] || false;
}

init.launch = async function (...modulesToLoad) {
  if (modulesToLoad.length === 0) {
    this.restart();
    return;
  }

  if (Array.isArray(modulesToLoad[0])) {
    modulesToLoad = modulesToLoad[0];
  }

  try {
    await initApi({ ...getPreference('openclassrooms')?.api, userId: App.user.id });

    dataConnectClientApiConfiguration.setAIClient(
      getAIClient({
        language: getActiveLang(),
      })
    );

    if (new URLSearchParams(browser.window.location.search).has('hasCurrentUserBeenEdited')) {
      await removeCachedCurrentUser();
    }
  } catch (e) {
    errorTracker.captureError(e, { type: 'launch error' });
  }

  modulesToLoad.forEach(initModule);
};

init.restart = function () {
  const startedModules = [];

  for (let i in App._launchedModules) {
    startedModules.push(App._launchedModules[i].name);
    App._launchedModules[i].init();
  }

  return startedModules;
};

export default init;
